import { DateTime } from 'luxon';

import { TableTH } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { List, ListStatus } from 'dashboard/models/list';
import {
  ListItem,
  ListItemBrowserFingerprint,
  ListItemCountry,
  ListItemDeviceFingerprint,
  ListItemEmailAddress,
  ListItemFace,
  ListItemField,
  ListItemGovernmentIdNumber,
  ListItemIpAddress,
  ListItemName,
  ListItemPhoneNumber,
  ListItemSelfieBackground,
  ListItemString,
} from 'dashboard/models/list-item';
import { AttributeColumn } from 'dashboard/models/table-view';
import { User } from 'dashboard/models/user';
import BrowserFingerprint from 'dashboard/views/DashboardListsShowView/ListItemTypes/BrowserFingerprint';
import Country from 'dashboard/views/DashboardListsShowView/ListItemTypes/Country';
import DeviceFingerprint from 'dashboard/views/DashboardListsShowView/ListItemTypes/DeviceFingerprint';
import EmailAddress from 'dashboard/views/DashboardListsShowView/ListItemTypes/EmailAddress';
import Face from 'dashboard/views/DashboardListsShowView/ListItemTypes/Face';
import Field from 'dashboard/views/DashboardListsShowView/ListItemTypes/Field';
import GovernmentIdNumber from 'dashboard/views/DashboardListsShowView/ListItemTypes/GovernmentIdNumber';
import IpAddress from 'dashboard/views/DashboardListsShowView/ListItemTypes/IpAddress';
import Name from 'dashboard/views/DashboardListsShowView/ListItemTypes/Name';
import PhoneNumber from 'dashboard/views/DashboardListsShowView/ListItemTypes/PhoneNumber';
import SelfieBackground from 'dashboard/views/DashboardListsShowView/ListItemTypes/SelfieBackground';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

import PlainString from './ListItemTypes/PlainString';

interface ListItemsTableNullStateProps {
  dataTestBlock?: string;
  listStatus?: ListStatus;
  handleShowAddListItemModal: () => void;
}

export interface SortableAttributeColumn extends AttributeColumn {
  sortable: boolean;
  timezone?: boolean;
}

const ListItemsTableNullState = ({ dataTestBlock, listStatus }: ListItemsTableNullStateProps) => (
  <TableNullState dataTestBlock={dataTestBlock}>
    <Card.Title>No items listed</Card.Title>
    {listStatus === ListStatus.Active ? (
      <Paragraph>Add an item to start tracking information across inquiries</Paragraph>
    ) : (
      <Paragraph>This list contains no active items as it was archived</Paragraph>
    )}
  </TableNullState>
);

interface ListItemsTableProps {
  list: List;
  listActive: boolean;
  listItems: ListItem[];
  users: User[];
  isLoading: boolean;
  sortParams: SortParams;
  removeListItem: (listItem: ListItem) => void;
  handleShowAddListItemModal: () => void;
  handleSortUpdate: (sortParams: SortParams) => void;
}

// eslint-disable-next-line react/display-name
export default ({
  list,
  listActive,
  listItems,
  users,
  isLoading,
  sortParams,
  removeListItem,
  handleShowAddListItemModal,
  handleSortUpdate,
}: ListItemsTableProps) => {
  const generateHeaders = (columns: SortableAttributeColumn[]) => {
    const handleColumnHeaderClick = (columnName: string) => {
      handleSortUpdate({
        sort: columnName,
        shouldSortDescending:
          columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
      });
    };
    const columnLabelMapping = (column: SortableAttributeColumn) => {
      if (column.timezone) {
        const tz: string = DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED);
        return `${column.label} (${tz})`;
      }
      return column.label;
    };

    return (
      <>
        {columns.map((column, i) => {
          return (
            <TableTH
              id={`list-items-table__header--${column.key}`}
              key={i}
              canSort={column.sortable}
              isDescending={sortParams.shouldSortDescending}
              isSorted={sortParams.sort === column.key}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              onClick={column.sortable ? () => handleColumnHeaderClick(column.key) : null}
            >
              {columnLabelMapping(column)}
            </TableTH>
          );
        })}
      </>
    );
  };

  const listType = list.type;
  const listItemTableProps = {
    generateHeaders,
    listActive,
    users,
    isLoading,
    removeListItem,
  };

  switch (listType) {
    case ApiModel.ListGovernmentIdNumber:
      return (
        <GovernmentIdNumber
          {...listItemTableProps}
          listItems={listItems as ListItemGovernmentIdNumber[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='government-id-number-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListIpAddress:
      return (
        <IpAddress
          {...listItemTableProps}
          listItems={listItems as ListItemIpAddress[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='ip-address-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListName:
      return (
        <Name
          {...listItemTableProps}
          listItems={listItems as ListItemName[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='name-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListPhoneNumber:
      return (
        <PhoneNumber
          {...listItemTableProps}
          listItems={listItems as ListItemPhoneNumber[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='phone-number-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListEmailAddress:
      return (
        <EmailAddress
          {...listItemTableProps}
          listItems={listItems as ListItemEmailAddress[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='email-address-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListBrowserFingerprint:
      return (
        <BrowserFingerprint
          {...listItemTableProps}
          listItems={listItems as ListItemBrowserFingerprint[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='browser-fingerprint-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListDeviceFingerprint:
      return (
        <DeviceFingerprint
          {...listItemTableProps}
          listItems={listItems as ListItemDeviceFingerprint[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='device-fingerprint-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListString:
      return (
        <PlainString
          {...listItemTableProps}
          listItems={listItems as ListItemString[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='string-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListFace:
      return (
        <Face
          {...listItemTableProps}
          listItems={listItems as ListItemFace[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='face-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListCountry:
      return (
        <Country
          {...listItemTableProps}
          listItems={listItems as ListItemCountry[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='country-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListField:
      return (
        <Field
          {...listItemTableProps}
          listItems={listItems as ListItemField[]}
          nullState={
            <ListItemsTableNullState
              dataTestBlock='field-table'
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    case ApiModel.ListGeolocation:
      throw new Error('Geolocation incorrectly rendered');
    case ApiModel.ListSelfieBackground:
      return (
        <SelfieBackground
          {...listItemTableProps}
          listItems={listItems as ListItemSelfieBackground[]}
          nullState={
            <ListItemsTableNullState
              listStatus={list.attributes.status}
              handleShowAddListItemModal={handleShowAddListItemModal}
            />
          }
        />
      );
    default:
      assertNever(listType);
  }
};
